import { useAxiosModule } from '@/modules/_shared/axios'

import * as types from './types'

import {
    MemberProfile,
    ChangeMemberHierarchyAssignment,
    ChangeMemberSupervisor,
    ChangeMemberReporting,
    ChangeMemberPhone, SupervisorsListModel, MemberOrganizationIdentifier
} from '@/modules/members/types'
import { ChangeOrganizationIndustry } from './types'

const {
    getFromApi,
    postToApi,
    uploadFileToApi,
    patchToApi,
    deleteInApi,
    apiUrl,
    mockAdapter
} = useAxiosModule()

/* Get Organizations List */

export type GetOrganizationsListResponse = types.GetOrganizationsList

export async function getOrganizationsList (search = '', orderBy = 'id desc', limit = 0, offset = 0) {
    const queryParams = new URLSearchParams()
    queryParams.set('search', search)
    queryParams.set('sort', orderBy)
    queryParams.set('limit', String(limit))
    queryParams.set('offset', String(offset))
    const url = 'organizations?' + queryParams.toString()
    return getFromApi<GetOrganizationsListResponse>(url)
}

/* Get Organizations simplified List */

export type GetOrganizationsSimplifiedListResponse = types.OrganizationsSimplifiedListModel[]
export const getOrganizationsSimplifiedList = async () =>
    getFromApi<GetOrganizationsSimplifiedListResponse>('organizations/simplified')

/* Get Organization */

export type GetOrganizationResponse = types.Organization

export const getOrganization = async (id: string) =>
    getFromApi<GetOrganizationResponse>(`organizations/${id}`)

/* Add Organization */

export type AddOrganizationRequest = types.AddOrganization
export const addOrganization = async (req: AddOrganizationRequest) =>
    await postToApi<null>('organizations/', req)

export const checkBusinessIdIsValid = async (businessId: string) =>
    await getFromApi<{ validityResult: number }>(`organizations/check-business-id-valid/${businessId}`)

export const checkBusinessIdIsAvailable = async (businessId: string) =>
    await getFromApi<{ validityResult: number }>(`organizations/check-business-id-available/${businessId}`)

/* Update Organization */

export type UpdateOrganizationRequest = types.Organization
mockAdapter.onPost(apiUrl('organization/1/update')).reply(200, null)

export const updateOrganization = async (id: string, req: UpdateOrganizationRequest) =>
    await patchToApi<null>(`organization/${id}/update`, req)

/* Update Organization name */

export type UpdateOrganizationNameRequest = types.ChangeOrganizationName
export const updateOrganizationName = async (id: string, req: UpdateOrganizationNameRequest) =>
    await patchToApi<null>(`organizations/${id}/change-name`, req)

/* Update Organization contact person */

export type UpdateContactPersonRequest = types.ChangeOrganizationContactPerson
export const updateOrganizationContactPerson = async (id: string, req: UpdateContactPersonRequest) =>
    await patchToApi<null>(`organizations/${id}/change-contact`, req)

export type UpdateOrganizationLogoUrlRequest = types.ChangeOrganizationLogoURL
export const updateOrganizationLogoURL = async (id: string, req: UpdateOrganizationLogoUrlRequest) =>
    await patchToApi<null>(`organizations/${id}/change-logo-url`, req)

export type UpdateOrganizationBusinessIdRequest = types.ChangeOrganizationBusinessId
export const updateOrganizationBusinessId = async (id: string, req: UpdateOrganizationBusinessIdRequest) =>
    await patchToApi<null>(`organizations/${id}/change-business-id`, req)

/* Get Organizations agreements simplified */

export type GetOrganizationAgreementsListSimplifiedResponse = types.AgreementSimple[]

mockAdapter.onGet(new RegExp('agreements/simplified/mock$')).reply<GetOrganizationAgreementsListSimplifiedResponse>(200, [
    {
        id: 'ce1bd27f-8b00-485d-a36d-daf521854d90',
        name: 'Some Agreement'
    },
    {
        id: '2333a579-13ca-4d59-a087-6e88e64d503f',
        name: 'Some Other Agreement'
    }
])

export const getOrganizationAgreementsSimplified = async (organizationId: string) =>
    getFromApi<GetOrganizationAgreementsListSimplifiedResponse>(`organizations/${organizationId}/agreements/simplified/mock`)

/* Get Organization Members List */

export type GetOrganizationMembersListResponse = types.OrganizationMembersListResponse
export async function getOrganizationMembersList (id: string, search = '', sortBy = 'id desc', limit = 0, offset = 0) {
    const queryParams = new URLSearchParams()
    queryParams.set('search', search)
    queryParams.set('sort', sortBy)
    queryParams.set('limit', String(limit))
    queryParams.set('offset', String(offset))
    const url = `organizations/${id}/members?` + queryParams.toString()
    return getFromApi<GetOrganizationMembersListResponse>(url)
}

/* Get Member Organization Profile */

export type GetOrganizationMemberProfileResponse = MemberProfile
export const getOrganizationMemberProfile = async (id: string, memberId: string) =>
    await getFromApi<GetOrganizationMemberProfileResponse>(`organizations/${id}/members/${memberId}`)

/* Add Member to Organization */

export type AddMemberToOrganizationRequest = types.AddMemberToOrganization
export const addMemberToOrganization = async (memberId: string, organizationId: string, req: AddMemberToOrganizationRequest) =>
    await postToApi(`organizations/${organizationId}/members/${memberId}`, req)

/* Delete Member from Organization */

export type DeleteMemberFromOrganizationRequest = types.DeleteMemberFromOrganization
export const deleteMemberFromOrganization = async (memberId: string, organizationId: string, req: DeleteMemberFromOrganizationRequest) =>
    deleteInApi<null>(`organizations/${organizationId}/members/${memberId}`, req)

/* Update Organization Member Phone */

export type UpdateMemberPhoneRequest = ChangeMemberPhone
export const updateOrganizationMemberPhone = async (id: string, memberId: string, req: UpdateMemberPhoneRequest) =>
    await patchToApi<null>(`organizations/${id}/members/${memberId}/change-profile-phone`, req)

/* Update Organization Member Hierarchy Assignment */

export type UpdateMemberHierarchyAssignmentRequest = ChangeMemberHierarchyAssignment
export const updateOrganizationMemberHierarchyAssignment = async (id: string, memberId: string, req: UpdateMemberHierarchyAssignmentRequest) =>
    await patchToApi<null>(`organizations/${id}/members/${memberId}/change-profile-hierarchy-assignment`, req)

/* Update Organization Member Supervisor */

export type UpdateMemberSupervisorRequest = ChangeMemberSupervisor
export const updateOrganizationMemberSupervisor = async (id: string, memberId: string, req: UpdateMemberSupervisorRequest) =>
    await patchToApi<null>(`organizations/${id}/members/${memberId}/assign-profile-supervisor`, req)

export type RemoveMemberSupervisorRequest = MemberOrganizationIdentifier
export const removeOrganizationMemberSupervisor = async (id: string, memberId: string, req: RemoveMemberSupervisorRequest) =>
    await patchToApi<null>(`organizations/${id}/members/${memberId}/remove-profile-supervisor`, req)

/* Update Organization Member Reporting */

export type UpdateMemberReportingRequest = ChangeMemberReporting
export const updateOrganizationMemberReporting = async (id: string, memberId: string, req: UpdateMemberReportingRequest) =>
    await patchToApi<null>(`organizations/${id}/members/${memberId}/change-profile-reporting`, req)

/* Get potential supervisors list */

export type GetSupervisorsListResponse = SupervisorsListModel[]
export const getSupervisors = async (id: string, memberId: string) =>
    await getFromApi<GetSupervisorsListResponse>(`organizations/${id}/members/${memberId}/potential-supervisors`)

/* Mass profile update */

export type MembersProfilesUpdateResponse = types.MembersProfilesUpdateModel[]
export const updateMembersProfiles = async (organizationId: string, file: File) =>
    await uploadFileToApi<MembersProfilesUpdateResponse>(`organizations/${organizationId}/multiple-profiles`, file)

/* Update organization industry */

export type UpdateOrganizationIndustryRequest = ChangeOrganizationIndustry
export async function updateSingleOrganizationIndustry (id: string, req: UpdateOrganizationIndustryRequest) {
    return patchToApi<null>(`organizations/${id}/change-industry`, req)
}

export async function turnOnSsoReportingDimensions (id: string, req: { organizationId: string }) {
    return patchToApi<null>(`organizations/${id}/sso_reporting_dimensions/on`, req)
}

export async function turnOffSsoReportingDimensions (id: string, req: { organizationId: string }) {
    return patchToApi<null>(`organizations/${id}/sso_reporting_dimensions/off`, req)
}
